import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { SMSProviders } from '../../../api';
import {
  PreparedProviderValues,
  SelectedProviders
} from '../../../api/configurations/providers/types';
import { Error } from '../../../api/setup/getApiError';
import { useConfig } from '../../../contexts/config';
import { useCurrentProject } from '../../../contexts/settings';
import { usePaths } from '../../../routes/routesHelper';
import { HorizontalCenterBlock, ShortSettingsBlock } from '../../../styled';
import {
  AlibabaCloudIcon,
  AmazonSES,
  CardGroup,
  FormFooter,
  HeaderTitle,
  SinchIcon,
  SmsCenterIcon,
  TelnyxIcon,
  TwilioIcon
} from '../../ui';
import { SettingsWrapper } from '../style';
import { AlibabaCloudFields } from './AlibabaCloudFields';
import { AmazonSESFields } from './AmazonSESFields';
import { SinchFields } from './SinchFields';
import { SmsCenterFields } from './SmsCenterFields';
import { TelnyxFields } from './TelnyxFields';
import { TwilioFields } from './TwilioFields';
import { FormValues, ProviderFieldProps } from './types';
import { SmsProvidersValidator } from './validator';

type Props = RouteComponentProps & {
  providersData?: PreparedProviderValues;
  fetching: boolean;
  submitError: Error | null;
  updateProviders: (
    values: PreparedProviderValues,
    selectedFields: Partial<SelectedProviders>
  ) => void;
};

const SMSProviderFields = {
  [SMSProviders.amazon]: (props: ProviderFieldProps) => <AmazonSESFields {...props} />,
  [SMSProviders.twilio]: (props: ProviderFieldProps) => <TwilioFields {...props} />,
  [SMSProviders.sinch]: (props: ProviderFieldProps) => <SinchFields {...props} />,
  [SMSProviders.telnyx]: (props: ProviderFieldProps) => <TelnyxFields {...props} />,
  [SMSProviders.smscenter]: (props: ProviderFieldProps) => <SmsCenterFields {...props} />,
  [SMSProviders.alibaba]: (props: ProviderFieldProps) => <AlibabaCloudFields {...props} />
};

const CustomSMSProvider = ({
  history,
  providersData,
  fetching = false,
  updateProviders,
  submitError = null
}: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();
  const { currentProject } = useCurrentProject();
  const { xsollaAnalytics } = useConfig();

  const backToNavigation = () =>
    currentProject && history.push({ pathname: navigationPath(currentProject.id) });

  const SubmitHandle = (values: FormValues) => {
    xsollaAnalytics.sendClick(
      'login_pa_module__custom_sms_provider',
      'submit',
      values.selected_providers
    );

    const changedProviders = Object.entries(values.selected_providers).reduce(
      (
        acc: Partial<SelectedProviders>,
        [providerName, isSelectedProvider]: [keyof SelectedProviders, boolean]
      ) => {
        if ((providersData && providerName in providersData) || isSelectedProvider) {
          acc[providerName] = isSelectedProvider;
        }
        return acc;
      },
      {}
    );

    updateProviders(values.fields, changedProviders);
  };

  const options = [
    {
      name: SMSProviders.amazon,
      label: intl.formatMessage({ id: 'custom-providers.settings.sms.provider.amazon' }),
      icon: <AmazonSES />,
      dataTestId: 'sms-provider-card-amazon'
    },
    {
      name: SMSProviders.twilio,
      label: intl.formatMessage({ id: 'custom-providers.settings.sms.provider.twilio' }),
      icon: <TwilioIcon />,
      dataTestId: 'sms-provider-card-twilio'
    },
    {
      name: SMSProviders.sinch,
      label: intl.formatMessage({ id: 'custom-providers.settings.sms.provider.sinch' }),
      icon: <SinchIcon />,
      dataTestId: 'sms-provider-card-sinch'
    },
    {
      name: SMSProviders.telnyx,
      label: intl.formatMessage({ id: 'custom-providers.settings.sms.provider.telnyx' }),
      icon: <TelnyxIcon />,
      dataTestId: 'sms-provider-card-telnyx'
    },
    {
      name: SMSProviders.smscenter,
      label: intl.formatMessage({ id: 'custom-providers.settings.sms.provider.smscenter' }),
      icon: <SmsCenterIcon />,
      dataTestId: 'sms-provider-card-smscenter'
    },
    {
      name: SMSProviders.alibaba,
      label: intl.formatMessage({ id: 'custom-providers.settings.sms.provider.alibaba' }),
      icon: <AlibabaCloudIcon />,
      dataTestId: 'sms-provider-card-alibaba',
      disabled: true
    }
  ];

  const defaultSelectedProviders = useMemo(() => {
    return Object.values(SMSProviders).reduce(
      (acc, item) => ({
        ...acc,
        [item]: !!providersData?.[item]?.name
      }),
      {}
    );
  }, [providersData]);

  const defaultValues = useMemo(() => {
    return Object.values(SMSProviders).reduce(
      (acc, item) => ({
        ...acc,
        [item]: providersData?.[item]?.name
          ? {
              app_secret: providersData[item].app_secret,
              app_id: providersData[item].app_id,
              aws_region: providersData[item].aws_region,
              phone_number: providersData[item].phone_number,
              sender_id: providersData[item].sender_id,
              name: providersData[item].name,
              is_enabled: providersData[item].is_enabled
            }
          : {
              app_secret: '',
              name: item
            }
      }),
      {}
    );
  }, [providersData]);

  return (
    <Form
      initialValues={{
        selected_providers: defaultSelectedProviders,
        fields: defaultValues
      }}
      validate={SmsProvidersValidator}
      onSubmit={SubmitHandle}>
      {({ handleSubmit, values: { fields, selected_providers }, pristine, errors }) => {
        return (
          <>
            <HeaderTitle
              title={intl.formatMessage({ id: 'custom-providers.settings.sms.title' })}
              subtitle={intl.formatMessage({ id: 'custom-providers.settings.sms.subtitle' })}
              notification={intl.formatMessage({
                id: 'custom-providers.settings.sms.notification'
              })}
            />

            <HorizontalCenterBlock>
              <ShortSettingsBlock>
                <SettingsWrapper>
                  <CardGroup
                    name="selected_providers"
                    defaultValue={defaultSelectedProviders}
                    rowLength={4}
                    options={options}
                  />
                </SettingsWrapper>
                {/* TODO: uncomment, when documentation will be ready */}
                {/* <SettingsWrapper> */}
                {/*   <ProviderSubtitle> */}
                {/*     {intl.formatMessage( */}
                {/*       { id: 'custom-providers.settings.sms.msp.read-more' }, */}
                {/*       { a: (chunk: string) => <DescriptionLink href="">{chunk}</DescriptionLink> } */}
                {/*     )} */}
                {/*   </ProviderSubtitle> */}
                {/* </SettingsWrapper> */}
                {Object.entries(selected_providers).map(([key, value]) => {
                  return (
                    value &&
                    SMSProviderFields[key as SMSProviders]?.({
                      errors: errors || {},
                      providerData: fields?.[key] || {}
                    })
                  );
                })}
              </ShortSettingsBlock>
            </HorizontalCenterBlock>

            <FormFooter
              onSubmit={handleSubmit}
              onCancel={backToNavigation}
              disabled={pristine}
              error={!!submitError}
              fetching={fetching}
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(CustomSMSProvider);
